import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { sendLogoutForm } from "@/api/auth";
import { restProvider } from "@/api/rest";
import { TwoFactorStatus, fetchTwoFactorStatus } from "@/api/twoFactor";
import { StatusChip } from "@/components/ui/StatusChip";
import { routePaths } from "@/config/routes";
import { useAuthContext } from "@/context/auth";
import { cn } from "@/lib/cn";
import { GetSelfResponse } from "@/lib/dto";

const menuItemClasses =
  "flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-foreground no-underline hover:bg-muted focus-visible:bg-muted focus-visible:outline-hidden";

const initialsOf = (user: GetSelfResponse) => {
  const fromName = `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`;
  return (fromName || user.username[0]).toUpperCase();
};

const ChevronDown = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 text-brand" aria-hidden="true" focusable="false">
    <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

/**
 * The account entry point on the screens outside the management app: who is signed in, the way to
 * the security settings and the way out. Both screens carry it, so someone who lands on either one
 * finds the same thing in the same corner.
 */
export const AccountMenu = () => {
  const { user } = useAuthContext();
  const { exitRunAs } = restProvider();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // The status is worth a request only once the menu is actually open: the products page must not
  // pay for it on every load. A failed request leaves the line out and the menu working - losing
  // the way to log out because a status call broke would be the worse failure.
  useEffect(() => {
    if (!open || status) {
      return;
    }

    fetchTwoFactorStatus()
      .then(setStatus)
      .catch(() => undefined);
  }, [open, status]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    // Escape returns to the trigger rather than dropping focus into the page, so the keyboard can
    // reopen the menu from where it left off.
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (!user) {
    return null;
  }

  const impersonator = user.runAs ? (user.impersonatorName ?? "an administrator") : null;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.username}${
          impersonator ? ` (impersonated by ${impersonator})` : ""
        }`}
        className="flex items-center gap-2 rounded-full border-none bg-transparent p-0.5 cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <span
          aria-hidden="true"
          className={cn(
            "grid h-10 w-10 place-items-center rounded-full bg-brand text-sm font-semibold text-white",
            impersonator && "ring-2 ring-warning-border ring-offset-2",
          )}
        >
          {initialsOf(user)}
        </span>
        <ChevronDown />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-2 w-72 overflow-hidden rounded-sm border border-border bg-white py-2 shadow-elegant"
        >
          <div className="border-b border-border px-4 pb-3 text-left">
            {fullName && <p className="m-0 text-sm font-semibold text-foreground">{fullName}</p>}
            <p className="m-0 text-sm text-muted-foreground">{user.username}</p>
            {impersonator && (
              <p className="m-0 mt-2">
                <StatusChip tone="warning">Impersonated by {impersonator}</StatusChip>
              </p>
            )}
          </div>

          <Link role="menuitem" to={routePaths.SECURITY} className={menuItemClasses}>
            <span>
              Account security
              {status && (
                <span className="block text-xs text-muted-foreground">
                  Two-step verification: {status.enabled ? "on" : "off"}
                </span>
              )}
            </span>
            {status && !status.enabled && (
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full bg-warning-border"
              />
            )}
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={user.runAs ? exitRunAs : sendLogoutForm}
            className={cn(menuItemClasses, "border-none bg-transparent cursor-pointer")}
          >
            {user.runAs ? "Exit impersonation" : "Log out"}
          </button>
        </div>
      )}
    </div>
  );
};
